import React, { useState } from "react";
import { MessageSquare, Send, Download, Share2, Users } from "lucide-react";
import { downloadOrderPdf, shareOrderPdf } from "@/lib/orderPdf";
import { toast } from "sonner";

/**
 * Action bar: WhatsApp text deep-links + PDF download + native share with PDF + photos attached.
 * Web Share API attaches the PDF directly to WhatsApp on mobile.
 * On desktop the PDF is downloaded so the user can attach it manually.
 */
export default function OrderShareBar({ order, adminPhone }) {
  const [busy, setBusy] = useState(false);

  const buildSummary = () => {
    const subtotal = order.total_amount || 0;
    const gstRate = order.gst_rate || 0.05;
    const gstAmount = order.gst_amount ?? Math.round(subtotal * gstRate);
    const grandTotal = order.grand_total ?? (subtotal + gstAmount);
    const qtyLine = [
      order.total_pcs ? `${order.total_pcs} PCS` : "",
      order.total_sets ? `${order.total_sets} SETS` : "",
    ].filter(Boolean).join(" + ") || `${order.total_quantity} PCS`;
    return [
      `*Order ${order.order_no}*`,
      ``,
      `*Customer Details*`,
      `Name: ${order.customer.name}`,
      `City: ${order.customer.city}`,
      `Phone: ${order.customer.phone}`,
      `Agent: ${order.customer.agent}${order.customer.agent_phone ? ` (${order.customer.agent_phone})` : ""}`,
      ``,
      `Total Quantity: ${qtyLine}`,
      `Total Order Value: ₹${Math.round(grandTotal).toLocaleString("en-IN")}`,
      ``,
      `With regards,`,
      ``,
      `MKB Designer Studio Pvt Ltd.`,
      `Kolkata`,
    ].join("\n");
  };

  const waLink = (phone) => {
    const clean = String(phone || "").replace(/[^\d]/g, "");
    const number = clean.length === 10 ? `91${clean}` : clean;
    return `https://wa.me/${number}?text=${encodeURIComponent(buildSummary())}`;
  };

  const handleShare = async () => {
    setBusy(true);
    try {
      const ok = await shareOrderPdf(order, adminPhone, buildSummary());
      if (!ok) {
        await downloadOrderPdf(order, adminPhone);
        toast.message("PDF downloaded — attach it in WhatsApp manually");
      }
    } catch (e) {
      toast.error("Failed to build PDF");
    } finally { setBusy(false); }
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      await downloadOrderPdf(order, adminPhone);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Failed to build PDF");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="order-share-bar">
      <div className="grid grid-cols-2 gap-3">
        <button onClick={handleShare} disabled={busy} className="brut-btn brut-btn-primary" data-testid="share-pdf-btn">
          <Share2 size={16} strokeWidth={3} /> {busy ? "PREPARING…" : "SHARE PDF"}
        </button>
        <button onClick={handleDownload} disabled={busy} className="brut-btn brut-btn-secondary" data-testid="download-pdf-btn">
          <Download size={16} strokeWidth={3} /> DOWNLOAD
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <a href={waLink(order.customer.phone)} target="_blank" rel="noreferrer"
           className="brut-btn brut-btn-dark" data-testid="wa-customer-btn">
          <MessageSquare size={16} strokeWidth={3} /> CUSTOMER
        </a>
        <a
          href={order.customer.agent_phone ? waLink(order.customer.agent_phone) : "#"}
          onClick={(e) => { if (!order.customer.agent_phone) { e.preventDefault(); toast.error("Agent phone not set"); } }}
          target="_blank" rel="noreferrer"
          className={`brut-btn brut-btn-dark ${!order.customer.agent_phone ? "opacity-60" : ""}`}
          data-testid="wa-agent-btn"
        >
          <Users size={16} strokeWidth={3} /> AGENT
        </a>
        <a href={waLink(adminPhone)} target="_blank" rel="noreferrer"
           className="brut-btn brut-btn-dark" data-testid="wa-admin-btn">
          <Send size={16} strokeWidth={3} /> ADMIN
        </a>
      </div>
    </div>
  );
}
